import _ from "lodash";
import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
import { Button, Tag, SearchInput } from "@toddle-edu/ds-web";
import UIModal from "UIComponents/UIModal";
import Loading from "UIComponents/Loading";
import { compose, graphql } from "lib/apolloClient";
import { getAllBillingTagsQuery } from "./modules/ManageTagsQueries";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import classes from "./ManageTagsModal.scss";
import {
  assignBillingTagsToOrganization,
  removeBillingTagsFromOrganization,
  createNewBillingTag,
} from "./modules/ManageTagsUtils";
import { connect } from "react-redux";
import { onEnterOrSpaceKeyDown } from "Utils";

const ManageTagsModal = props => {
  const { closeManageTagsModal, tags, orgTags, isLoading, isData } = props;

  const [searchText, setSearchText] = useState("");
  const [tagsList, setTagsList] = useState([]);
  const [selectedTagsList, setSelectedTagsList] = useState([]);
  const [isCreateNewTagBtnVisible, setIsCreateNewTagBtnVisible] = useState(
    false
  );

  useEffect(() => {
    if (_.isEmpty(tags)) return;
    setTagsList(tags);
  }, [tags]);

  useEffect(() => {
    if (_.isEmpty(orgTags) && _.isEmpty(tags)) return;
    const filteredTagsList = getFilteredList(tags, orgTags);
    setTagsList(filteredTagsList);
    setSelectedTagsList(orgTags);
  }, [orgTags, tags]);

  const getFilteredList = (listA, listB) => {
    if (_.isEmpty(listA) || _.isEmpty(listB)) return listA;
    const list = _.differenceWith(
      listA,
      listB,
      (listAItem, listBItem) => listAItem.id === listBItem.id
    );
    return list;
  };

  const onSearchChange = searchText => {
    const searchValue = _.trim(searchText);

    setSearchText(searchText);
    if (_.isEmpty(searchValue)) {
      const filteredTagsList = getFilteredList(tags, selectedTagsList);
      setTagsList(filteredTagsList);
      setIsCreateNewTagBtnVisible(false);
    } else {
      const searchFilter = searchValue.toUpperCase();
      const filteredTagsList = _.filter(tagsList, tagItem => {
        return tagItem.value.toUpperCase().indexOf(searchFilter) > -1;
      });
      if (_.isEmpty(filteredTagsList)) {
        setIsCreateNewTagBtnVisible(true);
      } else setIsCreateNewTagBtnVisible(false);
      setTagsList(filteredTagsList);
    }
  };

  const handleOnClickTag = ({ tag }) => {
    setSelectedTagsList([...selectedTagsList, tag]);
    const updatedTagsList = _.filter(
      tagsList,
      tagItem => tagItem.id !== tag.id
    );
    setTagsList(updatedTagsList);
  };

  const removeTagFromSelectedTagsList = ({ tag }) => {
    const updatedSelectedTagsList = _.filter(
      selectedTagsList,
      tagItem => tagItem.id !== tag.id
    );
    setSelectedTagsList(updatedSelectedTagsList);
    setTagsList([...tagsList, tag]);
  };

  const handleOnClickSave = () => {
    // assign tags to org
    const addedTagsList = getFilteredList(selectedTagsList, orgTags);
    const removedTagsList = getFilteredList(orgTags, selectedTagsList);
    if (_.isEmpty(addedTagsList) && _.isEmpty(removedTagsList)) {
      closeManageTagsModal();
      return;
    }
    assignRemoveBillingTags(addedTagsList, removedTagsList);
  };

  const assignRemoveBillingTags = async (
    addTagsList = [],
    removeTagList = []
  ) => {
    const {
      orgId,
      assignBillingTagsToOrganization,
      removeBillingTagsFromOrganization,
      setToastMsg,
      setIsLoading,
    } = props;

    try {
      setIsLoading(true);
      if (!_.isEmpty(addTagsList)) {
        await assignBillingTagsToOrganization({
          orgId,
          tags: selectedTagsList,
        });
      }
      if (!_.isEmpty(removeTagList)) {
        await removeBillingTagsFromOrganization({ orgId, tags: removeTagList });
      }
      closeManageTagsModal();
      setIsLoading(false);
      setToastMsg({
        msg: "toastMsgs:changes_saved_successfully",
        type: "success",
        position: "toast-bottom-left",
      });
    } catch (e) {
      setIsLoading(false);
      setToastMsg({
        msg: "toastMsgs:something_went_wrong",
        type: "warning",
        position: "toast-bottom-left",
      });
    }
  };

  const handleOnClickAddNewTag = () => {
    createNewTag();
  };

  const createNewTag = async () => {
    const { createNewBillingTag, setIsLoading } = props;
    setIsLoading(true);
    try {
      const newTag = await createNewBillingTag({
        tagValue: _.trim(searchText),
      });
      setIsCreateNewTagBtnVisible(false);
      setSelectedTagsList([...selectedTagsList, { ...newTag }]);
      setSearchText("");
      const tagsList = getFilteredList(tags, selectedTagsList);
      setTagsList(tagsList);
    } catch (e) {
      console.error("e", e);
    }
    setIsLoading(false);
  };

  const tagStyleClass = classNames(classes.tag, "text-label-2");

  return (
    <UIModal isOpen={true} modalContent={classes.modalContent}>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className="heading-3">Manage Tags</div>
        </div>
        <div className={classes.body}>
          <SearchInput
            value={searchText}
            placeholder="Search for tags"
            onChange={onSearchChange}
          />
          {!_.isEmpty(selectedTagsList) ? (
            <div className={classes.selectedTags}>
              {_.map(selectedTagsList, tag => {
                const { id, value } = tag;
                return (
                  <Tag
                    size="small"
                    key={id}
                    color="teal"
                    onClose={() => removeTagFromSelectedTagsList({ tag })}
                  >
                    {value}
                  </Tag>
                );
              })}
            </div>
          ) : null}
          {isLoading && !isData ? (
            <Loading />
          ) : (
            _.map(tagsList, tag => {
              const { id, value } = tag;
              return (
                <div
                  key={id}
                  className={tagStyleClass}
                  onClick={() => handleOnClickTag({ tag })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e =>
                    onEnterOrSpaceKeyDown(e, () => handleOnClickTag({ tag }))
                  }
                >
                  {value}
                </div>
              );
            })
          )}

          {isCreateNewTagBtnVisible ? (
            <div
              className={classes.createNewTag}
              onClick={handleOnClickAddNewTag}
              role="button"
              tabIndex={0}
              onKeyDown={e =>
                onEnterOrSpaceKeyDown(e, () => handleOnClickAddNewTag())
              }
            >
              <AddCircleOutlined />
              <div>
                {"Add new tag "}
                <b>{searchText}</b>
              </div>
            </div>
          ) : null}
        </div>
        <div className={classes.footer}>
          <Button variant="outlined-subtle" onClick={closeManageTagsModal}>
            Cancel
          </Button>
          <Button onClick={handleOnClickSave}>Save</Button>
        </div>
      </div>
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const orgTags = _.get(ownProps, "organization.tags", []);
  const orgId = _.get(ownProps, "organization.id", null);
  return { orgTags, orgId, isLoading: false };
};

const mapActionCreators = {
  assignBillingTagsToOrganization,
  removeBillingTagsFromOrganization,
  setToastMsg,
  createNewBillingTag,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAllBillingTagsQuery, {
    name: "getAllBillingTagsQuery",
    options: ({ keys = "BILLING_TAG" }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        input: {
          keys,
        },
      },
    }),
    props({ getAllBillingTagsQuery, ownProps }) {
      const allTags = _.get(getAllBillingTagsQuery, "platform.allTags", []);
      return {
        tags: allTags,
        isData: !_.isEmpty(allTags),
        isLoading:
          getAllBillingTagsQuery["networkStatus"] == 1 ||
          getAllBillingTagsQuery["networkStatus"] == 2 ||
          ownProps.isLoading,
      };
    },
  })
)(ManageTagsModal);
